import React, { FunctionComponent, useState } from "react";
import { Box } from "@mui/material";
import { formatDateTime } from "src/utils/date/dateHelper";
import { McGeneralDiseaseItem, mcGeneralDiseaseService } from "src/api/medcards/mcGeneralDiseaseService";
import { useTranslate } from "src/locales";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";

const dateIcon = "/assets/icons/clarity_date-line.svg";
const noteIcon = "/assets/icons/material-symbols_note-outline.svg";
const editIcon = "/assets/icons/mdi_pencil-outline.svg";
const trashIcon = "/assets/icons/mynaui_trash.svg";

interface DrugCardProps {
  name: string;
  operationDate: string;
  operator: string;
  note: string;
  dosePeriod: string;
  duration: string;
  id: number;
  originalItem?: McGeneralDiseaseItem;
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const InfoRow: FunctionComponent<{ icon: string; label: string; value: string }> = ({
  icon,
  label,
  value,
}) => (
  <Box
    sx={{
      alignSelf: "stretch",
      borderTop: "1px solid rgba(199, 199, 199, 0.3)",
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      padding: "6px 16px",
    }}
  >
    <Box
      sx={{
        alignSelf: "stretch",
        height: "32px",
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        padding: "8px 0px",
        boxSizing: "border-box",
        gap: "8px",
      }}
    >
      <Box component="img" src={icon} sx={{ height: "16px", width: "16px", position: "relative" }} alt="" />
      <Box sx={{ width: "110px", position: "relative", fontWeight: 600, display: "inline-block", flexShrink: 0 }}>
        {label}
      </Box>
      <Box sx={{ position: "relative", fontWeight: 300 }}>{value}</Box>
    </Box>
  </Box>
);

const DrugCard: FunctionComponent<DrugCardProps> = ({
  name,
  operationDate,
  operator,
  note,
  dosePeriod,
  duration,
  id,
  originalItem,
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const { t } = useTranslate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await mcGeneralDiseaseService.deleteGeneralDisease(id);
      onDelete?.(id);
      onDeleteSuccess?.();
      return true;
    } catch (error) {
      console.error("Error deleting drug:", error);
      onDeleteError?.();
      return false;
    }
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "318px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          textAlign: "left",
          fontSize: "12.01px",
          color: "#101010",
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <Box
          sx={{
            width: "318px",
            boxShadow:
              "0px 4.0019307136535645px 7px -5px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px rgba(199, 199, 199, 0.25)",
            borderRadius: "8px",
            backgroundColor: "#fff",
            overflow: "hidden",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              backgroundColor: "#f9f9f9",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: "8px 16px",
              fontSize: "16.01px",
            }}
          >
            <Box
              sx={{
                alignSelf: "stretch",
                minHeight: "48px",
                height: "auto",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "286px",
                  overflow: "hidden",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 0px",
                  boxSizing: "border-box",
                  gap: "8px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                    width: "100%",
                    gap: "8px",
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      position: "relative",
                      fontWeight: 600,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.4,
                      maxHeight: "2.8em",
                    }}
                  >
                    {name}
                  </Box>
                  <Box
                    component="img"
                    src={trashIcon}
                    alt={t("drug.delete")}
                    onClick={handleDeleteClick}
                    sx={{ width: "24px", position: "relative", maxHeight: "100%", cursor: "pointer" }}
                  />
                  <Box
                    component="img"
                    src={editIcon}
                    alt={t("drug.edit")}
                    onClick={() =>
                      onEdit?.(originalItem || {
                        id,
                        name,
                        manualdrugname: name,
                        pdate: operationDate,
                        ctime: operationDate,
                        cuser: operator,
                        note,
                        privatenotes: note,
                        dose: dosePeriod,
                        usagetime: duration ? Number.parseInt(duration.replace(/\D/g, ""), 10) : null,
                      } as McGeneralDiseaseItem)
                    }
                    sx={{ width: "24px", height: "24px", marginLeft: "8px", position: "relative", cursor: "pointer" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <InfoRow icon={dateIcon} label={t("drug.operation-date")} value={operationDate} />
        <InfoRow icon={dateIcon} label={t("drug.operation-user-short")} value={operator} />
        <InfoRow icon={noteIcon} label={t("drug.note-section")} value={note} />
        <InfoRow icon={noteIcon} label={t("drug.dose-period")} value={dosePeriod} />
        <InfoRow icon={noteIcon} label={t("drug.duration")} value={duration} />
      </Box>

      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDeleteConfirm}
        onSuccess={(success) => {
          if (success) {
            setDeleteDialogOpen(false);
          }
        }}
        title={t("drug.delete-confirm-title")}
        content={t("drug.delete-confirm-content")}
        successMessage={t("drug.delete-success")}
        errorMessage={t("drug.delete-error")}
        hideSnackbar={true}
      />
    </>
  );
};

interface DrugsCardListProps {
  data?: McGeneralDiseaseItem[];
  onDelete?: (id: number) => void;
  onEdit?: (item: McGeneralDiseaseItem) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: () => void;
}

const DrugsCardList: FunctionComponent<DrugsCardListProps> = ({
  data = [],
  onDelete,
  onEdit,
  onDeleteSuccess,
  onDeleteError,
}) => {
  const getUsageTimeText = (usagetime: number | null): string => {
    if (!usagetime) return "";
    // usagetime muhtemelen yıl cinsinden
    return `${usagetime} yıl`;
  };

  const items: DrugCardProps[] = data.map((item) => ({
    name: item.manualdrugname || item.name || "",
    operationDate: formatDateTime(item.pdate || item.ctime, "dd.MM.yyyy HH:mm") || "",
    operator: item.cuser || "",
    note: item.note || item.privatenotes || "",
    dosePeriod: item.dose || "",
    duration: getUsageTimeText(item.usagetime),
    id: item.id,
    originalItem: item,
    onDelete,
    onEdit,
    onDeleteSuccess,
    onDeleteError,
  }));

  if (items.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", marginTop: "12px" }}>
      {items.map((item, index) => (
        <DrugCard key={`${item.name}-${item.operationDate}-${index}`} {...item} />
      ))}
    </Box>
  );
};

export default DrugsCardList;
